<template>
  <SdfFloatingPane
    ref="paneEl"
    :style-bundles="['sdf-tooltip']"
    position-context="ELEMENT"
    mobile-behavior="floating"
    :attachment="attachment"
    :offset="offset"
    :portal-enabled="true"
    :single-mode="false"
  >
    <div
      :id="TOOLTIP_ID"
      role="tooltip"
      :class="TOOLTIP_CLASS"
      class="max-w-[var(--sdf-context-tooltip-layout-base-max-width,17.5rem)] whitespace-pre-line break-words rounded-lg border-0 border-solid bg-surface p-3 text-left text-sm font-normal leading-6 tracking-normal text-on-surface max-[600px]:max-w-[100vw]"
    >
      {{ tooltipOptions?.content }}
    </div>
  </SdfFloatingPane>
</template>

<script setup lang="ts">
import SdfFloatingPane from '@pequity/shared/ui/sdf-floating-pane/sdf-floating-pane.vue';
import { useWTooltip, wTooltipInstanceExists } from '@pequity/shared/ui/w-tooltip/useWTooltip';
import { nextFrame } from '@pequity/shared/ui/w-tooltip/w-tooltip.helpers';
import { TOOLTIP_CLASS, TOOLTIP_SELECTOR } from '@pequity/shared/ui/w-tooltip/w-tooltip.types';
import { useElementHover, useEventListener } from '@vueuse/core';
import { computed, ref, useTemplateRef, watch } from 'vue';

defineOptions({
  name: 'WTooltip',
});

const paneEl = useTemplateRef<InstanceType<typeof SdfFloatingPane>>('paneEl');
const { tooltipOptions, shouldTooltipShow, tooltipTrigger, setIsTooltipHovered, hideTooltip } = useWTooltip();
const tooltipContainerEl = ref<HTMLElement | null>(null);
const isTooltipContentHovered = useElementHover(tooltipContainerEl);
const activeTrigger = ref<HTMLElement | null>(null);
const activeTriggerInitialDescribedBy = ref<string | null>(null);

const TOOLTIP_ID = 'w-tooltip-content';

const attachment = computed(() => tooltipOptions.value?.placement ?? 'top');

const offset = computed(() => {
  const placement = attachment.value;

  if (['top', 'bottom'].includes(placement)) {
    return 16;
  }

  return 8;
});

const syncTooltipContainerElement = () => {
  const tooltipContainers = document.querySelectorAll<HTMLElement>(TOOLTIP_SELECTOR);
  tooltipContainerEl.value = tooltipContainers.item(tooltipContainers.length - 1) ?? null;
};

const setActiveTriggerDescribedBy = (trigger: HTMLElement) => {
  const existingDescribedBy = trigger.getAttribute('aria-describedby');
  activeTriggerInitialDescribedBy.value = existingDescribedBy;

  const describedByTokens = new Set((existingDescribedBy ?? '').split(/\s+/).filter(Boolean));
  describedByTokens.add(TOOLTIP_ID);

  trigger.setAttribute('aria-describedby', Array.from(describedByTokens).join(' '));
};

const clearActiveTriggerDescribedBy = () => {
  const trigger = activeTrigger.value;

  if (!trigger) {
    activeTriggerInitialDescribedBy.value = null;
    return;
  }

  const initialDescribedBy = activeTriggerInitialDescribedBy.value;

  if (initialDescribedBy && initialDescribedBy.trim().length > 0) {
    trigger.setAttribute('aria-describedby', initialDescribedBy);
  } else {
    trigger.removeAttribute('aria-describedby');
  }

  activeTriggerInitialDescribedBy.value = null;
};

const isTooltipContainerElement = (target: EventTarget | null) => {
  return target instanceof Element && target.closest(TOOLTIP_SELECTOR) !== null;
};

const onDocumentMouseOut = (event: MouseEvent) => {
  if (!tooltipOptions.value?.stayOpenOnHover) return;

  const leavingTooltip = isTooltipContainerElement(event.target);
  const enteringTooltip = isTooltipContainerElement(event.relatedTarget);

  if (leavingTooltip && !enteringTooltip) {
    setIsTooltipHovered(false);
    hideTooltip();
  }
};

const onDocumentKeyDown = (event: KeyboardEvent) => {
  if (event.key !== 'Escape' || !shouldTooltipShow.value) return;

  setIsTooltipHovered(false);
  hideTooltip();
};

useEventListener(document, 'mouseout', onDocumentMouseOut);
useEventListener(document, 'keydown', onDocumentKeyDown);

watch(isTooltipContentHovered, (isHovered) => {
  setIsTooltipHovered(isHovered);

  if (!isHovered && tooltipOptions.value?.stayOpenOnHover) {
    hideTooltip();
  }
});

watch([shouldTooltipShow, tooltipTrigger], async ([shouldShow, trigger], _, onCleanup) => {
  let isCancelled = false;

  onCleanup(() => {
    isCancelled = true;
  });

  if (!shouldShow || !trigger) {
    clearActiveTriggerDescribedBy();
    activeTrigger.value = null;
    tooltipContainerEl.value = null;
    await paneEl.value?.close();
    return;
  }

  if (trigger === activeTrigger.value) return;

  clearActiveTriggerDescribedBy();
  activeTrigger.value = trigger;
  setActiveTriggerDescribedBy(trigger);
  await paneEl.value?.open(trigger);

  if (isCancelled) {
    await paneEl.value?.close();
    return;
  }

  await nextFrame();

  if (isCancelled) {
    await paneEl.value?.close();
    return;
  }

  syncTooltipContainerElement();

  if (!shouldTooltipShow.value) {
    clearActiveTriggerDescribedBy();
    activeTrigger.value = null;
    await paneEl.value?.close();
  }
});

// Check if the WTooltip instance already exists
if (wTooltipInstanceExists.value) {
  throw new Error('WTooltip can only be used once per application. Remove duplicate instances.');
}

wTooltipInstanceExists.value = true;
</script>
